import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/EquityManagement.css';

const EquityManagement = () => {
  const { openContactModal } = useModal();

  return (
    <div className="equity-management">
      {/* Hero Section */}
      <section className="equity-hero">
        <div className="equity-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="equity-hero-content"
          >
            <div className="hero-badge">
              📈 Equity Solutions
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Professional <span class=\"gradient-text\">Equity Management</span><br />Solutions" }}
            />
            
            <p className="hero-subtitle">
              Streamline your equity management processes with our comprehensive solutions for cap table management, valuations, and regulatory compliance.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'Equity Management Services',
                  'Equity Management Page - Get Started Today'
                )}
              >
                Get Started Today
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'Equity Management Information',
                  'Equity Management Page - Learn More'
                )}
              >
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="equity-section">
        <div className="equity-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="equity-section-title gradient-text">Equity Management Services</h2>
            <p className="equity-section-subtitle">
              Comprehensive solutions to manage your company's equity efficiently and compliantly
            </p>
            
            <div className="equity-services-grid">
              
              <div className="equity-glass-card">
                <h3>Cap Table Management</h3>
                <p>Maintain accurate and up-to-date capitalization tables with detailed ownership tracking.</p>
                <ul>
                  <li>✓ Ownership Tracking & Analysis</li>
                  <li>✓ Share Class Management</li>
                  <li>✓ Waterfall Modeling</li>
                  <li>✓ Scenario Planning</li>
                </ul>
              </div>
              
              <div className="equity-glass-card">
                <h3>Equity Valuations</h3>
                <p>Professional 409A valuations and equity assessments for compliance and decision making.</p>
                <ul>
                  <li>✓ 409A Valuations</li>
                  <li>✓ ASC 718 Compliance</li>
                  <li>✓ Fair Value Assessments</li>
                  <li>✓ IRC Section 83(b) Elections</li>
                </ul>
              </div>
              
              <div className="equity-glass-card">
                <h3>Stock Option Administration</h3>
                <p>End-to-end management of employee stock option plans and equity compensation programs.</p>
                <ul>
                  <li>✓ Plan Design & Implementation</li>
                  <li>✓ Grant Administration</li>
                  <li>✓ Vesting Schedules</li>
                  <li>✓ Exercise Processing</li>
                </ul>
              </div>
              
              <div className="equity-glass-card">
                <h3>Regulatory Compliance</h3>
                <p>Ensure compliance with securities regulations and reporting requirements.</p>
                <ul>
                  <li>✓ SEC Compliance</li>
                  <li>✓ State Securities Laws</li>
                  <li>✓ Blue Sky Filings</li>
                  <li>✓ Audit Support</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="equity-section">
        <div className="equity-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="equity-section-title gradient-text">Why Choose Our Equity Services</h2>
            <p className="equity-section-subtitle">
              Expert guidance and technology-driven solutions for modern equity management
            </p>
            
            <div className="equity-benefits-grid">
              
              <div className="equity-benefit-card">
                <div className="equity-benefit-icon">⚖️</div>
                <h4 className="equity-benefit-title">Regulatory Expertise</h4>
                <p className="equity-benefit-description">Deep knowledge of securities laws and compliance requirements</p>
              </div>
              
              <div className="equity-benefit-card">
                <div className="equity-benefit-icon">📊</div>
                <h4 className="equity-benefit-title">Advanced Analytics</h4>
                <p className="equity-benefit-description">Sophisticated modeling and scenario analysis capabilities</p>
              </div>
              
              <div className="equity-benefit-card">
                <div className="equity-benefit-icon">🔒</div>
                <h4 className="equity-benefit-title">Secure Platform</h4>
                <p className="equity-benefit-description">Bank-grade security for sensitive equity information</p>
              </div>
              
              <div className="equity-benefit-card">
                <div className="equity-benefit-icon">🚀</div>
                <h4 className="equity-benefit-title">Scalable Solutions</h4>
                <p className="equity-benefit-description">Grows with your company from startup to public offering</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="equity-section">
        <div className="equity-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="equity-cta-card">
              <h3 className="equity-cta-title">
                Ready to Streamline Your Equity Management?
              </h3>
              <p className="equity-cta-subtitle">
                Contact us today to learn how our equity management solutions can simplify your cap table, ensure compliance, and support your growth.
              </p>
              <div className="equity-cta-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'Equity Management Setup',
                    'Equity Management Page - Start Managing Equity'
                  )}
                >
                  Start Managing Equity
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'Equity Management Demo',
                    'Equity Management Page - Schedule Demo'
                  )}
                >
                  Schedule Demo
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EquityManagement;